import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isNativePlatform } from '../lib/platform.js';
import { useSheetStackStore } from '../store/sheet-stack-store.js';
import { useAiDrawerStore } from '../store/ai-drawer-store.js';
import { useToastStore } from '../store/toast-store.js';

/** Tabs where back should exit the app rather than navigate (per role). */
const ROOT_TAB_PATHS = new Set(['/', '/overview', '/messages', '/my-work', '/tasks', '/entry']);

const DOUBLE_PRESS_WINDOW_MS = 2000;

/**
 * Android hardware back button priority (android-app-plan §6.3 / M2.5):
 *   1. close the top-most open sheet (popups, drawers, filters)
 *   2. close the AI Co-Pilot panel
 *   3. router back
 *   4. on a root tab: double-press-to-exit with a toast hint
 *
 * No-op on web — the listener only registers inside the native shell.
 */
export function useAndroidBackButton(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const lastBackPressRef = useRef(0);

  // Keep latest values readable from the native listener without re-binding.
  const stateRef = useRef({ pathname: location.pathname, navigate });
  stateRef.current = { pathname: location.pathname, navigate };

  useEffect(() => {
    if (!isNativePlatform()) return;

    let removed = false;
    let remove: (() => void) | null = null;

    void (async () => {
      const { App } = await import('@capacitor/app');
      const sub = await App.addListener('backButton', () => {
        if (useSheetStackStore.getState().closeTop()) return;

        const aiDrawer = useAiDrawerStore.getState();
        if (aiDrawer.isOpen) {
          aiDrawer.close();
          return;
        }

        const { pathname, navigate: nav } = stateRef.current;
        if (!ROOT_TAB_PATHS.has(pathname)) {
          nav(-1);
          return;
        }

        const now = Date.now();
        if (now - lastBackPressRef.current < DOUBLE_PRESS_WINDOW_MS) {
          void App.exitApp();
          return;
        }
        lastBackPressRef.current = now;
        useToastStore.getState().show('Press back again to exit');
      });
      if (removed) {
        void sub.remove();
      } else {
        remove = () => void sub.remove();
      }
    })();

    return () => {
      removed = true;
      remove?.();
    };
  }, []);
}
